import React, { useState, useEffect } from 'react';
import { financeAPI } from '../../lib/api';
import { Plus, RefreshCw, Search, Eye, Trash2 } from 'lucide-react';

const JournalEntries = () => {
    const [entries, setEntries] = useState([]);
    const [chartOfAccounts, setChartOfAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewingEntry, setViewingEntry] = useState(null);
    
    const [formData, setFormData] = useState({
        entry_date: new Date().toISOString().split('T')[0],
        description: '',
        reference: '',
        items: [{ chart_of_account_id: '', debit: 0, credit: 0 }]
    });
    const [successMessage, setSuccessMessage] = useState('');

    // Generate auto reference when modal opens
    const generateReference = () => {
        const now = new Date();
        const dateStr = now.getFullYear().toString() + 
            String(now.getMonth() + 1).padStart(2, '0') + 
            String(now.getDate()).padStart(2, '0') + 
            String(now.getHours()).padStart(2, '0') + 
            String(now.getMinutes()).padStart(2, '0') + 
            String(now.getSeconds()).padStart(2, '0');
        return `JE-001-${dateStr}`;
    };

    useEffect(() => { 
        fetchEntries(); 
        fetchChartOfAccounts();
    }, []);

    const fetchEntries = async () => {
        try {
            setLoading(true);
            const response = await financeAPI.getJournalEntries();
            setEntries(response.data.data || response.data);
        } catch (err) { setError('Failed to load data'); }
        finally { setLoading(false); }
    };

    const fetchChartOfAccounts = async () => {
        try {
            const response = await financeAPI.getChartOfAccounts();
            const data = response.data.data || response.data;
            setChartOfAccounts(Array.isArray(data) ? data : []);
        } catch (err) { 
            console.error('Failed to load chart of accounts'); 
        }
    };

    // Helper function to display account info
    const getAccountDisplay = (accountId) => {
        if (!accountId) return '';
        const account = chartOfAccounts.find(a => a.id === parseInt(accountId));
        if (account) {
            return `${account.account_code} - ${account.account_name}`;
        }
        return `ID: ${accountId}`;
    };

    // Helper function to format date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        const options = { day: '2-digit', month: 'short', year: 'numeric' };
        return date.toLocaleDateString('en-GB', options);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');
        
        // Convert items to the format expected by backend
        const formattedData = {
            entry_date: formData.entry_date,
            description: formData.description,
            reference: formData.reference,
            items: formData.items.map(item => ({
                chart_of_account_id: parseInt(item.chart_of_account_id),
                debit: parseFloat(item.debit) || 0,
                credit: parseFloat(item.credit) || 0
            }))
        };
        
        try {
            const response = await financeAPI.createJournalEntry(formattedData);
            const createdEntry = response.data?.data || response.data;
            
            // Show success message with the reference number
            if (createdEntry && createdEntry.reference) {
                setSuccessMessage(`Journal entry created successfully! Reference: ${createdEntry.reference}`);
            } else {
                setSuccessMessage('Journal entry created successfully!');
            }
            
            setTimeout(() => {
                setShowModal(false);
                setSuccessMessage('');
                resetForm();
                fetchEntries();
            }, 2000); // Close modal after 2 seconds
        } catch (err) { 
            setError(err.response?.data?.message || 'Failed to save'); 
        }
    };

    const resetForm = () => {
        setFormData({ 
            entry_date: new Date().toISOString().split('T')[0], 
            description: '', 
            reference: '', 
            items: [{ chart_of_account_id: '', debit: 0, credit: 0 }] 
        });
    };

    const addItem = () => {
        setFormData({ ...formData, items: [...formData.items, { chart_of_account_id: '', debit: 0, credit: 0 }] });
    };

    const updateItem = (index, field, value) => {
        const newItems = [...formData.items];
        
        if (field === 'chart_of_account_id') {
            newItems[index][field] = value ? parseInt(value) : '';
        } else if (field === 'debit' || field === 'credit') {
            newItems[index][field] = value;
            // Clear the other field when entering amount
            if (field === 'debit' && value > 0) {
                newItems[index].credit = 0;
            } else if (field === 'credit' && value > 0) {
                newItems[index].debit = 0;
            }
        } else {
            newItems[index][field] = value;
        }
        
        setFormData({ ...formData, items: newItems });
    };

    const removeItem = (index) => {
        if (formData.items.length > 1) {
            const newItems = formData.items.filter((_, i) => i !== index);
            setFormData({ ...formData, items: newItems });
        }
    };

    const filteredEntries = entries.filter(entry => 
        entry.description?.toLowerCase().includes(searchTerm.toLowerCase()) || 
        entry.reference?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Calculate totals
    const calculateTotals = () => {
        let totalDebit = 0;
        let totalCredit = 0;
        formData.items.forEach(item => {
            totalDebit += parseFloat(item.debit) || 0;
            totalCredit += parseFloat(item.credit) || 0;
        });
        return { totalDebit, totalCredit };
    };

    const totals = calculateTotals();

    return (
        <div>
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Journal Entries</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Manage journal entries</p>
                </div>
                <button onClick={() => { resetForm(); setFormData(prev => ({...prev, reference: generateReference()})); setShowModal(true); }} className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg">
                    <Plus className="w-4 h-4 mr-2" /> New Entry
                </button>
            </div>

            {error && <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>}
            {successMessage && <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">{successMessage}</div>}

            <div className="mb-4 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-800 dark:text-gray-200" />
            </div>

            <div className="bg-white dark:bg-slate-700 rounded-lg shadow overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center"><RefreshCw className="w-8 h-8 animate-spin mx-auto text-indigo-600" /></div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-slate-600">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Reference</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Description</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Amount</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-slate-600">
                                {filteredEntries.length > 0 ? filteredEntries.map(entry => (
                                    <tr key={entry.id} className="hover:bg-gray-50 dark:hover:bg-slate-600">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{formatDate(entry.entry_date)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300 font-mono">{entry.reference || '-'}</td>
                                        <td className="px-6 py-4 text-sm text-gray-800 dark:text-gray-200">{entry.description}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800 dark:text-gray-200 font-medium">
                                            {entry.total_debit || 0}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                            <button onClick={() => setViewingEntry(entry)} className="text-indigo-600 hover:text-indigo-900 mr-3">
                                                <Eye className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="5" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">No entries found</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* New Entry Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-slate-700 rounded-lg p-6 w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
                        <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-100">New Journal Entry</h2>
                        {successMessage && <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded text-sm font-medium">{successMessage}</div>}
                        <form onSubmit={handleSubmit}>
                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date <span className="text-red-500">*</span></label>
                                    <input 
                                        type="date" 
                                        value={formData.entry_date} 
                                        onChange={(e) => setFormData({...formData, entry_date: e.target.value})} 
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" 
                                        required 
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reference</label>
                                    <input 
                                        type="text" 
                                        value={formData.reference} 
                                        onChange={(e) => setFormData({...formData, reference: e.target.value})} 
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" 
                                    />
                                </div>
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description <span className="text-red-500">*</span></label>
                                <textarea 
                                    value={formData.description} 
                                    onChange={(e) => setFormData({...formData, description: e.target.value})} 
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200" 
                                    rows="2" 
                                    required 
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Journal Items <span className="text-red-500">*</span></label>
                                <div className="bg-gray-50 dark:bg-slate-600 rounded-lg p-3">
                                    {/* Header */}
                                    <div className="grid grid-cols-12 gap-2 mb-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                                        <div className="col-span-5">Account</div>
                                        <div className="col-span-3">Debit</div>
                                        <div className="col-span-3">Credit</div>
                                        <div className="col-span-1"></div>
                                    </div>
                                    
                                    {/* Items */}
                                    {formData.items.map((item, index) => (
                                        <div key={index} className="grid grid-cols-12 gap-2 mb-2 items-center">
                                            <div className="col-span-5">
                                                <select
                                                    value={item.chart_of_account_id || ''}
                                                    onChange={(e) => updateItem(index, 'chart_of_account_id', e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-500 rounded-lg bg-white dark:bg-slate-700 text-gray-800 dark:text-gray-200 text-sm"
                                                    required
                                                >
                                                    <option value="">Select Account</option>
                                                    {chartOfAccounts.map(account => (
                                                        <option key={account.id} value={account.id}>
                                                            {account.account_code} - {account.account_name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-span-3">
                                                <input 
                                                    type="number" 
                                                    step="0.01" 
                                                    min="0"
                                                    placeholder="0.00"
                                                    value={item.debit || ''} 
                                                    onChange={(e) => updateItem(index, 'debit', e.target.value)} 
                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-500 rounded-lg bg-white dark:bg-slate-700 text-gray-800 dark:text-gray-200 text-sm" 
                                                />
                                            </div>
                                            <div className="col-span-3">
                                                <input 
                                                    type="number" 
                                                    step="0.01" 
                                                    min="0"
                                                    placeholder="0.00"
                                                    value={item.credit || ''} 
                                                    onChange={(e) => updateItem(index, 'credit', e.target.value)} 
                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-500 rounded-lg bg-white dark:bg-slate-700 text-gray-800 dark:text-gray-200 text-sm" 
                                                />
                                            </div>
                                            <div className="col-span-1 flex justify-center">
                                                <button 
                                                    type="button" 
                                                    onClick={() => removeItem(index)} 
                                                    className="text-red-600 hover:text-red-800 p-1"
                                                    disabled={formData.items.length === 1}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                    
                                    <button 
                                        type="button" 
                                        onClick={addItem} 
                                        className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                                    >
                                        + Add Item
                                    </button>
                                </div>
                                
                                {/* Totals */}
                                <div className="mt-3 flex justify-end gap-8 text-sm">
                                    <div className="flex gap-2">
                                        <span className="text-gray-500">Total Debit:</span>
                                        <span className={`font-medium ${totals.totalDebit !== totals.totalCredit ? 'text-red-600' : 'text-green-600'}`}>
                                            {totals.totalDebit.toFixed(2)}
                                        </span>
                                    </div>
                                    <div className="flex gap-2">
                                        <span className="text-gray-500">Total Credit:</span>
                                        <span className={`font-medium ${totals.totalDebit !== totals.totalCredit ? 'text-red-600' : 'text-green-600'}`}>
                                            {totals.totalCredit.toFixed(2)}
                                        </span>
                                    </div>
                                </div>
                                
                                {totals.totalDebit !== totals.totalCredit && (
                                    <p className="mt-2 text-xs text-red-600 text-right">
                                        Warning: Debit and Credit must be equal
                                    </p>
                                )}
                            </div>
                            
                            <div className="flex justify-end gap-3">
                                <button 
                                    type="button" 
                                    onClick={() => setShowModal(false)} 
                                    className="px-4 py-2 border border-gray-300 dark:border-slate-500 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-600"
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-50"
                                    disabled={totals.totalDebit !== totals.totalCredit || totals.totalDebit === 0}
                                >
                                    Save
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* View Entry Modal */}
            {viewingEntry && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-slate-700 rounded-lg p-6 w-full max-w-2xl mx-4">
                        <div className="flex justify-between items-start mb-4">
                            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Journal Entry Details</h2>
                            <button onClick={() => setViewingEntry(null)} className="text-gray-500 hover:text-gray-700">×</button>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                            <div>
                                <span className="text-gray-500">Date:</span>
                                <span className="ml-2 text-gray-800 dark:text-gray-200">{formatDate(viewingEntry.entry_date)}</span>
                            </div>
                            <div>
                                <span className="text-gray-500">Reference:</span>
                                <span className="ml-2 text-gray-800 dark:text-gray-200">{viewingEntry.reference || '-'}</span>
                            </div>
                            <div className="col-span-2">
                                <span className="text-gray-500">Description:</span>
                                <span className="ml-2 text-gray-800 dark:text-gray-200">{viewingEntry.description}</span>
                            </div>
                        </div>

                        <div className="border-t border-gray-200 dark:border-slate-600 pt-4">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 dark:bg-slate-600">
                                    <tr>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Account</th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Debit</th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Credit</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-slate-600">
                                    {(viewingEntry.items || []).map((item, idx) => (
                                        <tr key={idx}>
                                            <td className="px-3 py-2 text-gray-800 dark:text-gray-200">
                                                {item.chart_of_account?.account_name || `Account ID: ${item.chart_of_account_id}`}
                                            </td>
                                            <td className="px-3 py-2 text-right text-gray-800 dark:text-gray-200">
                                                {item.type === 'debit' ? (item.amount || 0) : '-'}
                                            </td>
                                            <td className="px-3 py-2 text-right text-gray-800 dark:text-gray-200">
                                                {item.type === 'credit' ? (item.amount || 0) : '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="bg-gray-50 dark:bg-slate-600">
                                    <tr>
                                        <td className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300">Total</td>
                                        <td className="px-3 py-2 text-right font-medium text-gray-700 dark:text-gray-300">
                                            {viewingEntry.total_debit || 0}
                                        </td>
                                        <td className="px-3 py-2 text-right font-medium text-gray-700 dark:text-gray-300">
                                            {viewingEntry.total_credit || 0}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        <div className="mt-4 flex justify-end">
                            <button 
                                onClick={() => setViewingEntry(null)} 
                                className="px-4 py-2 border border-gray-300 dark:border-slate-500 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default JournalEntries;
